/**
 * LeaderboardDisplay - Phaser UI component for displaying the global leaderboard
 */

import Phaser from 'phaser';
import { LeaderboardManager, HighScore, GlobalLeaderboardState } from '../utils/LeaderboardManager';
import { UI_CONFIG, GAME_WIDTH, GAME_HEIGHT } from '../config/game.config';
import { leaderboardService } from '../services/LeaderboardService';

interface LeaderboardDisplayConfig {
  x: number;
  y: number;
  width: number;
  maxEntries?: number;
  showPlayerRank?: boolean;
  showLiveIndicator?: boolean;
  title?: string;
}

export class LeaderboardDisplay {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private config: Required<LeaderboardDisplayConfig>;
  private unsubscribe: (() => void) | null = null;

  // UI elements
  private titleText!: Phaser.GameObjects.Text;
  private liveIndicator!: Phaser.GameObjects.Container;
  private entriesContainer!: Phaser.GameObjects.Container;
  private playerRankText!: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, config: LeaderboardDisplayConfig) {
    this.scene = scene;
    this.config = {
      maxEntries: 5,
      showPlayerRank: true,
      showLiveIndicator: true,
      title: 'GLOBAL LEADERBOARD',
      ...config,
    };

    this.container = scene.add.container(config.x, config.y);
    this.createUI();
    this.subscribeToUpdates();
  }

  private createUI(): void {
    // Title
    this.titleText = this.scene.add
      .text(this.config.width / 2, 0, this.config.title, {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: UI_CONFIG.colors.primary,
      })
      .setOrigin(0.5, 0);

    this.container.add(this.titleText);

    // Live indicator (pulsing dot)
    if (this.config.showLiveIndicator) {
      this.createLiveIndicator();
    }

    // Entries container
    this.entriesContainer = this.scene.add.container(0, 35);
    this.container.add(this.entriesContainer);

    // Player rank (shown below the list)
    if (this.config.showPlayerRank) {
      this.playerRankText = this.scene.add
        .text(this.config.width / 2, 35 + this.config.maxEntries * 25 + 15, '', {
          fontFamily: 'monospace',
          fontSize: '14px',
          color: '#FFD700',
        })
        .setOrigin(0.5, 0);

      this.container.add(this.playerRankText);
    }
  }

  private createLiveIndicator(): void {
    this.liveIndicator = this.scene.add.container(this.config.width - 10, 8);

    // Pulsing circle
    const dot = this.scene.add.circle(0, 0, 4, 0x00ff00);
    const label = this.scene.add
      .text(-20, 0, 'LIVE', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#00ff00',
      })
      .setOrigin(1, 0.5);

    this.liveIndicator.add([label, dot]);
    this.container.add(this.liveIndicator);

    // Pulse animation
    this.scene.tweens.add({
      targets: dot,
      alpha: { from: 1, to: 0.3 },
      scale: { from: 1, to: 0.7 },
      duration: 800,
      yoyo: true,
      repeat: -1,
    });
  }

  private subscribeToUpdates(): void {
    this.unsubscribe = LeaderboardManager.subscribe((state) => {
      this.updateDisplay(state);
    });
  }

  private updateDisplay(state: GlobalLeaderboardState): void {
    // Update live indicator
    if (this.liveIndicator) {
      this.liveIndicator.setVisible(state.isConnected);
    }

    // Clear existing entries
    this.entriesContainer.removeAll(true);

    // Get scores to display
    const scores = LeaderboardManager.getHighScores().slice(0, this.config.maxEntries);
    const playerId = leaderboardService.getPlayerId();

    // Render entries
    scores.forEach((score, index) => {
      const entry = this.createScoreEntry(score, index, score.name === playerId);
      this.entriesContainer.add(entry);
    });

    // Show message if no scores
    if (scores.length === 0) {
      const noScoresText = this.scene.add
        .text(this.config.width / 2, 40, state.isConnected ? 'No scores yet!' : 'Offline - Local scores only', {
          fontFamily: 'monospace',
          fontSize: '12px',
          color: '#888888',
        })
        .setOrigin(0.5, 0);

      this.entriesContainer.add(noScoresText);
    }

    // Update player rank
    if (this.playerRankText) {
      const playerRank = LeaderboardManager.getPlayerRank();
      if (playerRank !== null) {
        this.playerRankText.setText(`YOUR RANK: #${playerRank}`);
        this.playerRankText.setVisible(true);
      } else {
        this.playerRankText.setVisible(false);
      }
    }
  }

  private createScoreEntry(
    score: HighScore,
    index: number,
    isCurrentPlayer: boolean
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(0, index * 25);
    const yOffset = 0;

    // Rank
    const rankText = this.scene.add
      .text(5, yOffset, `${score.rank || index + 1}.`, {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: this.getRankColor(score.rank || index + 1),
      })
      .setOrigin(0, 0);

    // Name
    const displayName = (score.name || 'Anonymous').slice(0, 12);
    const nameText = this.scene.add
      .text(35, yOffset, displayName, {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: isCurrentPlayer ? '#FFD700' : UI_CONFIG.colors.white,
      })
      .setOrigin(0, 0);

    // Score
    const scoreText = this.scene.add
      .text(this.config.width - 10, yOffset, score.score.toString().padStart(6, ' '), {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: isCurrentPlayer ? '#FFD700' : UI_CONFIG.colors.white,
      })
      .setOrigin(1, 0);

    container.add([rankText, nameText, scoreText]);

    // Highlight current player
    if (isCurrentPlayer) {
      const highlight = this.scene.add.rectangle(
        this.config.width / 2,
        yOffset + 7,
        this.config.width - 5,
        22,
        0xffd700,
        0.15
      );
      container.addAt(highlight, 0);
    }

    // Local score indicator
    if (score.isLocal) {
      const localIndicator = this.scene.add
        .text(this.config.width - 60, yOffset, '(local)', {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#666666',
        })
        .setOrigin(1, 0);
      container.add(localIndicator);
    }

    return container;
  }

  private getRankColor(rank: number): string {
    switch (rank) {
      case 1:
        return '#FFD700'; // Gold
      case 2:
        return '#C0C0C0'; // Silver
      case 3:
        return '#CD7F32'; // Bronze
      default:
        return UI_CONFIG.colors.primary;
    }
  }

  /**
   * Show a "New High Score" animation when player achieves a high score
   */
  showNewScoreAnimation(rank: number): void {
    const celebrationText = this.scene.add
      .text(this.config.width / 2, -20, `#${rank} WORLDWIDE!`, {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: '#FFD700',
      })
      .setOrigin(0.5);

    this.container.add(celebrationText);

    // Animate
    this.scene.tweens.add({
      targets: celebrationText,
      y: -30,
      alpha: { from: 1, to: 0 },
      scale: { from: 1, to: 1.5 },
      duration: 2000,
      ease: 'Power2',
      onComplete: () => {
        celebrationText.destroy();
      },
    });
  }

  /**
   * Refresh the leaderboard from server
   */
  async refresh(): Promise<void> {
    await LeaderboardManager.refreshGlobalLeaderboard();
  }

  /**
   * Set visibility
   */
  setVisible(visible: boolean): this {
    this.container.setVisible(visible);
    return this;
  }

  /**
   * Get the container for positioning
   */
  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.container.destroy();
  }
}

/**
 * Create a full-screen leaderboard overlay for game over screen
 */
export function createGameOverLeaderboard(
  scene: Phaser.Scene,
  playerScore: number,
  isWin: boolean,
  playerRank: number | null
): Phaser.GameObjects.Container {
  const container = scene.add.container(0, 0);

  // Dark overlay
  const overlay = scene.add.rectangle(
    GAME_WIDTH / 2,
    GAME_HEIGHT / 2,
    GAME_WIDTH,
    GAME_HEIGHT,
    0x000000,
    0.85
  );
  container.add(overlay);

  // Title
  const titleText = isWin ? 'VICTORY!' : 'GAME OVER';
  const titleColor = isWin ? UI_CONFIG.colors.primary : UI_CONFIG.colors.danger;

  const title = scene.add
    .text(GAME_WIDTH / 2, 60, titleText, {
      fontFamily: 'monospace',
      fontSize: UI_CONFIG.fontSize.gameOver,
      color: titleColor,
    })
    .setOrigin(0.5);
  container.add(title);

  // Score
  const scoreY = 120;
  const scoreLabel = scene.add
    .text(GAME_WIDTH / 2, scoreY, `FINAL SCORE: ${playerScore}`, {
      fontFamily: 'monospace',
      fontSize: '24px',
      color: UI_CONFIG.colors.white,
    })
    .setOrigin(0.5);
  container.add(scoreLabel);

  // Global rank
  if (playerRank !== null) {
    const rankText = scene.add
      .text(GAME_WIDTH / 2, scoreY + 35, `GLOBAL RANK: #${playerRank}`, {
        fontFamily: 'monospace',
        fontSize: '20px',
        color: '#FFD700',
      })
      .setOrigin(0.5);
    container.add(rankText);

    // Celebration for top 10
    if (playerRank <= 10) {
      const celebrationText = scene.add
        .text(GAME_WIDTH / 2, scoreY + 65, getPlacementMessage(playerRank), {
          fontFamily: 'monospace',
          fontSize: '16px',
          color: '#FFD700',
        })
        .setOrigin(0.5);

      container.add(celebrationText);

      // Pulsing animation
      scene.tweens.add({
        targets: celebrationText,
        scale: { from: 1, to: 1.1 },
        duration: 400,
        yoyo: true,
        repeat: -1,
      });
    }
  }

  // Leaderboard
  const leaderboardY = playerRank !== null && playerRank <= 10 ? 220 : 190;

  const leaderboardTitle = scene.add
    .text(GAME_WIDTH / 2, leaderboardY, 'GLOBAL TOP SCORES', {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: UI_CONFIG.colors.primary,
    })
    .setOrigin(0.5);
  container.add(leaderboardTitle);

  // Connection status
  const isOnline = LeaderboardManager.isOnline();
  const statusText = scene.add
    .text(GAME_WIDTH / 2 + 120, leaderboardY, isOnline ? 'LIVE' : 'OFFLINE', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: isOnline ? '#00ff00' : '#ff6666',
    })
    .setOrigin(0.5);
  container.add(statusText);

  // Score entries
  const scores = LeaderboardManager.getHighScores().slice(0, 5);
  const playerId = leaderboardService.getPlayerId();

  scores.forEach((score, index) => {
    const yPos = leaderboardY + 35 + index * 28;
    const isCurrentPlayer = score.name === playerId ||
      (score.timestamp && Math.abs(score.timestamp - Date.now()) < 5000 && score.score === playerScore);
    const textColor = isCurrentPlayer ? '#FFD700' : UI_CONFIG.colors.white;

    const rankColor = index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : UI_CONFIG.colors.primary;

    const rankLabel = scene.add
      .text(GAME_WIDTH / 2 - 150, yPos, `${index + 1}.`, {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: rankColor,
      });

    const nameLabel = scene.add
      .text(GAME_WIDTH / 2 - 120, yPos, (score.name || 'Anonymous').slice(0, 12), {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: textColor,
      });

    const scoreValue = scene.add
      .text(GAME_WIDTH / 2 + 100, yPos, score.score.toString().padStart(6, ' '), {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: textColor,
      });

    container.add([rankLabel, nameLabel, scoreValue]);

    if (isCurrentPlayer) {
      const highlight = scene.add.rectangle(
        GAME_WIDTH / 2,
        yPos + 8,
        320,
        24,
        0xffd700,
        0.15
      );
      container.addAt(highlight, 1);
    }
  });

  // Show message if no global scores
  if (scores.length === 0) {
    const noScores = scene.add
      .text(GAME_WIDTH / 2, leaderboardY + 60, isOnline ? 'Be the first to set a high score!' : 'Playing offline', {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#888888',
      })
      .setOrigin(0.5);
    container.add(noScores);
  }

  return container;
}

function getPlacementMessage(rank: number): string {
  switch (rank) {
    case 1:
      return '*** WORLD CHAMPION! ***';
    case 2:
      return '** SILVER MEDAL! **';
    case 3:
      return '* BRONZE MEDAL! *';
    default:
      return `TOP ${rank} WORLDWIDE!`;
  }
}
